import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SkillGroupSelector from './components/SkillGroupSelector';
import ResultsList from './components/ResultsList';
import LoadingIndicator from './components/LoadingIndicator';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import './App.css';

function App() {
  const [mainSkillOptions, setMainSkillOptions] = useState([]);
  const [skillGroups, setSkillGroups] = useState([
    { mainSkillId: null, skillProficiency: [], subSkills: [] }
  ]);
  const [subSkillOptionsMap, setSubSkillOptionsMap] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNoResults, setShowNoResults] = useState(false);

  // Fetch main skills on mount
  useEffect(() => {
    fetch('https://skillsdatabasefa.azurewebsites.net/api/loadskills?code=MgYGNZ2r-Z1GNaIWZoQ4xkoVftfPjQDOpz9juGO1ECaxAzFuSfNymg==&is_parent=1')
      .then(res => res.json())
      .then(data => {
        setMainSkillOptions(data.map(item => ({ id: String(item.skill_id), name: item.skill_name })));
      })
      .catch(() => setMainSkillOptions([]));
  }, []);

  // Fetch subskills for each group when mainSkillId changes
  useEffect(() => {
    skillGroups.forEach((group) => {
      if (group.mainSkillId && !subSkillOptionsMap[group.mainSkillId]) {
        fetch(`https://skillsdatabasefa.azurewebsites.net/api/loadskills?code=MgYGNZ2r-Z1GNaIWZoQ4xkoVftfPjQDOpz9juGO1ECaxAzFuSfNymg==&is_parent=0&parent_id=${group.mainSkillId}`)
        .then(res => res.json())
          .then(data => {
            setSubSkillOptionsMap(prev => ({ ...prev, [group.mainSkillId]: data.map(item => ({ id: String(item.skill_id), name: item.skill_name })) }));
          })
          .catch(() => setSubSkillOptionsMap(prev => ({ ...prev, [group.mainSkillId]: [] })));
    }
    });
  }, [skillGroups, subSkillOptionsMap]);

  const handleMainSkillChange = (idx, mainSkillId) => {
    setSkillGroups(groups => groups.map((g, i) => i === idx ? { mainSkillId, skillProficiency: [], subSkills: [] } : g));
  };
  const handleSkillProficiencyChange = (idx, skillProficiency) => {
    setSkillGroups(groups => groups.map((g, i) => i === idx ? { ...g, skillProficiency } : g));
  };
  const handleSubSkillsChange = (idx, subSkillIds) => {
    setSkillGroups(groups => groups.map((g, i) =>
      i === idx
        ? {
            ...g,
            subSkills: subSkillIds.map(subSkillId => {
              const existing = g.subSkills.find(s => s.subSkillId === subSkillId);
              return existing ? existing : { subSkillId, proficiency: [] };
            })
          }
        : g
    ));
  };
  const handleSubSkillProficiencyChange = (groupIdx, subSkillId, proficiency) => {
    setSkillGroups(groups => groups.map((g, i) =>
      i === groupIdx
        ? {
            ...g,
            subSkills: g.subSkills.map(s =>
              s.subSkillId === subSkillId ? { ...s, proficiency } : s
            )
          }
        : g
    ));
  };
  const handleRemoveGroup = (idx) => {
    setSkillGroups(groups => groups.length === 1 ? groups : groups.filter((_, i) => i !== idx));
  };
  const handleAddGroup = () => {
    setSkillGroups(groups => [...groups, { mainSkillId: null, skillProficiency: [], subSkills: [] }]);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setShowNoResults(false);
    // Build an array of skill queries (one per subskill in each group)
    const skillQueries = skillGroups
      .filter(g => g.mainSkillId && g.subSkills.length > 0)
      .flatMap(g => {
        const subSkillOptions = subSkillOptionsMap[g.mainSkillId] || [];
        return g.subSkills.map(sub => {
          const subSkill = subSkillOptions.find(opt => opt.id === sub.subSkillId);
          const proficiency = sub.proficiency && sub.proficiency.length > 0
            ? sub.proficiency
            : g.skillProficiency;
          return {
            name: subSkill ? subSkill.name : sub.subSkillId,
            min_proficiency: proficiency && proficiency.length > 0 ? Math.min(...proficiency) : 1
          };
        });
      });
    try {
      // Make one API call per skill query
      const allResults = await Promise.all(
        skillQueries.map(skill =>
          fetch('https://skillsdatabasefa.azurewebsites.net/api/runquery?code=MgYGNZ2r-Z1GNaIWZoQ4xkoVftfPjQDOpz9juGO1ECaxAzFuSfNymg==', {
        method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({ skills: [skill] })
          }).then(res => {
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            return res.json();
          })
        )
      );
      // Intersect results by employee name
      const intersection = allResults.reduce((acc, curr) => {
        if (acc === null) return curr;
        const currNames = new Set(curr.map(e => e.name));
        return acc.filter(e => currNames.has(e.name));
      }, null);

      // Build a map of employee name -> all matched skills/proficiencies
      const employeeSkillsMap = {};
      intersection?.forEach(emp => {
        employeeSkillsMap[emp.name] = { ...emp, matchedSkills: [] };
      });
      // For each API result, add the matched skill to the employee's matchedSkills
      allResults.forEach((result, idx) => {
        result.forEach(emp => {
          if (employeeSkillsMap[emp.name]) {
            // Find the skill for this query
            const skillQuery = skillQueries[idx];
            // Find the matching skill in emp.skills
            const matched = emp.skills.find(s => s.skill_name === skillQuery.name && (!skillQuery.min_proficiency || s.proficiency_level >= skillQuery.min_proficiency));
            if (matched) {
              employeeSkillsMap[emp.name].matchedSkills.push({
                skill_name: matched.skill_name,
                proficiency_level: matched.proficiency_level
              });
            }
          }
        });
      });
      // Convert map to array
      const enrichedResults = Object.values(employeeSkillsMap);
      setResults(enrichedResults || []);
      setLoading(false);
      if (!enrichedResults || enrichedResults.length === 0) {
        setShowNoResults(true);
        setError('No results found. Try adjusting your search criteria.');
      } else {
        setError(null);
      }
    } catch (error) {
      setError(`Failed to perform search: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        minWidth: '100vw',
        width: '100vw',
        background: '#eef2f9',
        backgroundSize: 'cover',
            display: 'flex',
        flexDirection: 'column',
        py: { xs: 2, sm: 4 },
        boxSizing: 'border-box',
        overflow: 'auto',
          }}
        >
      {/* Header: Logo left, Title center */}
      <Box flexShrink={0} display="flex" alignItems="center" width={{ xs: 120, sm: 180 }} pl={{ xs: 1, sm: 4 }}>
        <img src="/LogoRELI.png" alt="RELI Group Logo" style={{ height: 30, maxWidth: '100%' }} />
      </Box>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" width="100%" mb={4} px={1}>
        <Box mx={{ xs: 1, sm: 4 }} px={{ xs: 2, sm: 4 }} py={1.5} borderRadius={2} bgcolor="#4a90e2">
          <Typography variant="h4" fontWeight={700} color="#fff" align="center" sx={{ letterSpacing: 1, fontSize: { xs: '1.3rem', sm: '2rem', md: '2.5rem' } }}>
            Employee Skill Search
          </Typography>
        </Box>
      </Box>
      {/* Main Content: Two Columns */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          justifyContent: 'center',
          width: '100%',
          maxWidth: 1600,
          flexGrow: 1,
          gap: { xs: 2, md: 4 },
          px: { xs: 1, sm: 2, md: 4 },
          boxSizing: 'border-box',
          overflow: 'auto',
        }}
      >
        {/* Left: Skill Groups and Add Button */}
        <Box
          flex={1}
          minWidth={0}
          maxWidth={{ xs: '100%', md: 420 }}
          px={{ xs: 0, sm: 2, md: 3 }}
          py={{ xs: 2, md: 4 }}
          sx={{ width: '100%', boxSizing: 'border-box' }}
        >
          {skillGroups.map((group, idx) => (
            <SkillGroupSelector
              key={idx}
              mainSkillOptions={mainSkillOptions}
              subSkillOptions={group.mainSkillId ? (subSkillOptionsMap[group.mainSkillId] || []) : []}
              selectedMainSkill={group.mainSkillId}
              skillProficiency={group.skillProficiency}
              subSkills={group.subSkills}
              onMainSkillChange={mainSkillId => handleMainSkillChange(idx, mainSkillId)}
              onSkillProficiencyChange={proficiency => handleSkillProficiencyChange(idx, proficiency)}
              onSubSkillsChange={subSkillIds => handleSubSkillsChange(idx, subSkillIds)}
              onSubSkillProficiencyChange={(subSkillId, proficiency) => handleSubSkillProficiencyChange(idx, subSkillId, proficiency)}
              onRemove={skillGroups.length > 1 ? () => handleRemoveGroup(idx) : undefined}
              loading={loading}
            />
          ))}
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddGroup}
              sx={{
                fontWeight: 'bold',
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                py: 1,
                background: 'linear-gradient(90deg, #ff9800 0%, #f44336 100%)',
                color: '#fff',
                boxShadow: 3,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                minWidth: 0,
                width: 'auto',
                maxWidth: '100%',
                '&:hover': {
                  background: 'linear-gradient(90deg, #f44336 0%, #ff9800 100%)',
                  color: '#fff',
                },
              }}
            >
              Add More Skills
            </Button>
          </Box>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              size="large"
            onClick={handleSearch}
              disabled={loading}
              sx={{
                minWidth: 0,
                width: 'auto',
                fontWeight: 'bold',
                px: { xs: 3, sm: 4 },
                py: 1.5,
                borderRadius: 2,
                boxShadow: 3,
                background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                color: '#fff',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                '&:hover': {
                  background: 'linear-gradient(90deg, #38ef7d 0%, #11998e 100%)',
                  color: '#fff',
                },
              }}
          >
              {'SEARCH'}
            </Button>
          </Box>
        </Box>
        {/* Right: Results Table */}
        <Box
          flex={2}
          minWidth={0}
          maxWidth={900}
          px={{ xs: 0, sm: 2, md: 3 }}
          py={{ xs: 2, md: 4 }}
          ml={{ xs: 0, md: 6 }}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" align="center" mb={2} fontWeight={700} color="#343a40" mt={0} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' } }}>
            Search Results
          </Typography>
          <Box sx={{ width: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', background: '#ffffff' }}>
            <Box sx={{ maxHeight: { xs: '80vh', md: 520 }, overflow: 'auto' }}>
              {loading ? <LoadingIndicator /> : <ResultsList results={results} error={error} />}
            </Box>
          </Box>
          <Dialog open={showNoResults} onClose={() => setShowNoResults(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ textAlign: 'center', color: 'error.main', fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
              No Results Found
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', pb: 0, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Try adjusting your search criteria.
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button onClick={() => setShowNoResults(false)} variant="contained" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, px: 3 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
}

export default App;
